<template>
  <div class="w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-5">
    <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-4">{{ $t('tagListViewer.title', 'Tags') }}</h3>
    <div class="flex flex-col gap-3">
      <div v-for="tag in sortedTags" :key="tag.name" class="flex items-center gap-3">
        <!-- Tag Pill -->
        <div class="w-1/3 flex-shrink-0 flex justify-end pr-2">
          <span 
            class="px-2 py-0.5 rounded-lg inline-flex items-center justify-center font-semibold text-[10px] whitespace-nowrap"
            :style="getTagStyle(tag)">
            {{ getTagName(tag) }}
          </span>
        </div>
        <!-- Progress Bar -->
        <div class="flex-grow flex items-center">
            <div class="h-1 w-full bg-gray-50 dark:bg-slate-700/50 rounded-full overflow-hidden">
                <div class="h-full rounded-full transition-all duration-500" :style="{ width: getPercentage(tag) + '%', backgroundColor: getTagStyle(tag).backgroundColor }"></div>
            </div>
        </div>
        <!-- Amount -->
        <div class="w-6 text-right text-xs text-gray-400 dark:text-gray-500 font-medium">
          {{ tag.amount }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { generateTagColors, getLocalizedTagName } from '@/service/tagColor.js';

const { locale } = useI18n();

const props = defineProps({
  tags: {
    type: Array,
    required: true,
    default: () => [] // array of { name: '...', amount: Number, ... }
  }
});

const getTagName = (tag) => {
  return getLocalizedTagName(tag, locale.value);
};

const sortedTags = computed(() => {
    // Sort by amount descending, then by localized name
    return [...props.tags].sort((a, b) => {
        if (b.amount !== a.amount) return b.amount - a.amount;
        return getTagName(a).localeCompare(getTagName(b));
    });
});

const maxAmount = computed(() => {
    if (props.tags.length === 0) return 1;
    return Math.max(...props.tags.map(t => t.amount));
});

const getPercentage = (tag) => {
    if (maxAmount.value === 0) return 0;
    return (tag.amount / maxAmount.value) * 100;
};

const getTagStyle = (tag) => {
    const bg = tag.color_background;
    const fg = tag.color_font;
    if (bg && fg) {
        return { backgroundColor: bg, color: fg };
    }
    const c = generateTagColors(tag.name);
    return { backgroundColor: c.color_background, color: c.color_font };
};
</script>
